import React, { useState, useEffect } from 'react';
import supabase from '../supabase/supabase';
import { formatPHP, formatDate } from '../utils/format';
import { startOfWeek, endOfWeek, eachDayOfInterval, format, subWeeks, isSameDay } from 'date-fns';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiTrendingUp, FiPieChart, FiCalendar, FiTarget, FiArrowRight, FiActivity } = FiIcons;

const TABLE_EXPENSES = 'expenses_1739880000000';

const Insights = () => {
  const [loading, setLoading] = useState(true);
  const [insights, setInsights] = useState({
    dailyBreakdown: [],
    topSuppliers: [],
    totalSpent: 0,
    avgDaily: 0,
    peakDay: { date: '', amount: 0 }
  });

  useEffect(() => {
    fetchInsights();
  }, []);

  const fetchInsights = async () => {
    try {
      setLoading(true);
      const today = new Date();
      const weekStart = startOfWeek(today, { weekStartsOn: 0 });
      const weekEnd = endOfWeek(today, { weekStartsOn: 0 });

      const { data: expenses, error } = await supabase
        .from(TABLE_EXPENSES)
        .select('*')
        .gte('purchase_date', format(weekStart, 'yyyy-MM-dd'))
        .lte('purchase_date', format(weekEnd, 'yyyy-MM-dd'));

      if (error) throw error;

      // 1. Daily Breakdown
      const days = eachDayOfInterval({ start: weekStart, end: weekEnd });
      const dailyData = days.map(day => {
        const dayExpenses = expenses?.filter(e => isSameDay(new Date(e.purchase_date), day)) || [];
        const total = dayExpenses.reduce((sum, e) => sum + Number(e.amount), 0);
        return {
          dayName: format(day, 'EEE'),
          date: format(day, 'MMM d'),
          amount: total
        };
      });

      // 2. Top Suppliers
      const supplierMap = {};
      expenses?.forEach(e => {
        supplierMap[e.supplier] = (supplierMap[e.supplier] || 0) + Number(e.amount);
      });
      const topSuppliers = Object.entries(supplierMap)
        .map(([name, amount]) => ({ name, amount }))
        .sort((a, b) => b.amount - a.amount)
        .slice(0, 5);

      // 3. Stats
      const totalSpent = expenses?.reduce((sum, e) => sum + Number(e.amount), 0) || 0;
      const peakDay = [...dailyData].sort((a, b) => b.amount - a.amount)[0];
      
      setInsights({
        dailyBreakdown: dailyData,
        topSuppliers,
        totalSpent,
        avgDaily: totalSpent / 7,
        peakDay: peakDay || { date: 'N/A', amount: 0 }
      });

    } catch (err) {
      console.error('Error fetching insights:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  const maxDaily = Math.max(...insights.dailyBreakdown.map(d => d.amount), 1);

  return (
    <div className="space-y-8 pb-10">
      <header>
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Spending Insights</h1>
        <p className="text-gray-500 mt-1">Detailed analysis of your weekly expenditure</p>
      </header>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-4">
            <div className="w-10 h-10 bg-emerald-50 text-emerald-600 rounded-xl flex items-center justify-center">
              <SafeIcon icon={FiActivity} />
            </div>
            <span className="text-xs font-bold text-emerald-600 bg-emerald-50 px-2 py-1 rounded-full">Weekly</span>
          </div>
          <p className="text-sm font-medium text-gray-500">Avg. Daily Spend</p>
          <h3 className="text-2xl font-bold text-gray-900 mt-1">{formatPHP(insights.avgDaily)}</h3>
        </div>

        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-4">
            <div className="w-10 h-10 bg-orange-50 text-orange-600 rounded-xl flex items-center justify-center">
              <SafeIcon icon={FiTrendingUp} />
            </div>
          </div>
          <p className="text-sm font-medium text-gray-500">Peak Spending Day</p>
          <h3 className="text-2xl font-bold text-gray-900 mt-1">{insights.peakDay.dayName} ({formatPHP(insights.peakDay.amount)})</h3>
        </div>

        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-4">
            <div className="w-10 h-10 bg-blue-50 text-blue-600 rounded-xl flex items-center justify-center">
              <SafeIcon icon={FiTarget} />
            </div>
          </div>
          <p className="text-sm font-medium text-gray-500">Total Transactions</p>
          <h3 className="text-2xl font-bold text-gray-900 mt-1">{insights.dailyBreakdown.reduce((acc, curr) => acc + (curr.amount > 0 ? 1 : 0), 0)} Active Days</h3>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Daily Bar Chart */}
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
          <h2 className="text-lg font-bold text-gray-900 mb-6 flex items-center">
            <SafeIcon icon={FiCalendar} className="mr-2 text-emerald-600" />
            Daily Spend Distribution
          </h2>
          <div className="flex items-end justify-between h-48 pt-4">
            {insights.dailyBreakdown.map((day, idx) => (
              <div key={idx} className="flex flex-col items-center flex-1 group">
                <div className="relative w-full px-1 flex flex-col items-center">
                   <div 
                    className="absolute bottom-full mb-2 bg-gray-900 text-white text-[10px] py-1 px-2 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10"
                  >
                    {formatPHP(day.amount)}
                  </div>
                  <div 
                    className="w-full max-w-[24px] bg-emerald-500 rounded-t-md transition-all duration-500 ease-out hover:bg-emerald-600"
                    style={{ height: `${(day.amount / maxDaily) * 140}px`, minHeight: day.amount > 0 ? '4px' : '0px' }}
                  ></div>
                </div>
                <span className="text-[10px] font-bold text-gray-400 mt-3 uppercase">{day.dayName}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Top Suppliers */}
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
          <h2 className="text-lg font-bold text-gray-900 mb-6 flex items-center">
            <SafeIcon icon={FiPieChart} className="mr-2 text-emerald-600" />
            Top Suppliers
          </h2>
          <div className="space-y-5">
            {insights.topSuppliers.length === 0 ? (
              <p className="text-center text-gray-500 py-10">No data available for this week.</p>
            ) : (
              insights.topSuppliers.map((sup, idx) => (
                <div key={idx} className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="font-medium text-gray-700">{sup.name}</span>
                    <span className="font-bold text-gray-900">{formatPHP(sup.amount)}</span>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
                    <div 
                      className="bg-emerald-500 h-full rounded-full"
                      style={{ width: `${(sup.amount / insights.totalSpent) * 100}%` }}
                    ></div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Insights;